// --------------------------------------------------------------
// Created On: 2021-05-14
// Author: Zachary Thomas
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import useReduxSelector from "src/hooks/useReduxSelector";
import HamburgerButton from "./HamburgerButton/HamburgerButton";
import { RPM_COMPANY, PUBLIC_PAGES, APP_IS_IN_DEV_ENV } from "../../constants/miscellaneous";
import { Link } from "react-router-dom";
import userHasCrossCompanyAccess from "../../utilities/userHasCrossCompanyAccess";
import CompanyOptions from "./CompanyOptions/CompanyOptions";
import UserOptions from "./UserOptions/UserOptions";
import Spinner from "../Spinner/Spinner";
import ShoppingCartButton from "./ShoppingCartButton/ShoppingCartButton";
import SearchBar from "../SearchBar/SearchBar";
import styles from "./Navbar.module.scss";

// General navbar.
export default function Navbar(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [startSearch, setStartSearch] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);
  const inCompany = currentUser.companyName !== RPM_COMPANY;

  return (
    <nav className={`${styles.body} navbar navbar-expand-xl px-3`}>
      <Spinner loading={loading} />

      {/* Navbar content. */}
      <div className="w-100">
        <div className="row align-items-center">
          <div className={`${styles.start} col-auto`}>
            {/* Hamburger menu button. */}
            <HamburgerButton onClick={() => props.onOpenNavigationDrawer()} />

            {/* Cornell logo. */}
            <Link className={`${styles.brand} p-0 m-0`} to={PUBLIC_PAGES.includes(props.currentPage) ? "/login" : "/"}>
              <img className={`${styles.logo} m-0`} src="/logo.png" alt="Shop Cornell Pump" />
              {APP_IS_IN_DEV_ENV && (
                <img className={styles.logoOverlay} src="/images/dev_overlay.svg" alt="Dev Logo Overlay" />
              )}
            </Link>
          </div>

          {/* Search bar for BOM, serial, and part numbers. */}
          {!PUBLIC_PAGES.includes(props.currentPage) && (
            <Fragment>
              <div className={`col justify-content-center ${styles.searchBarFullContent}`}>
                <SearchBar
                  isInNavbar={true}
                  isInNavDrawer={false}
                  isInDashboard={false}
                  startSearch={startSearch}
                  errorMessage={errorMessage}
                  onSearchComplete={() => setStartSearch(false)}
                  onErrorMessageChange={(errorMessage) => setErrorMessage(errorMessage)}
                  onLoadingChange={(loading) => setLoading(loading)}
                  onClose={() => {}}
                />
              </div>
            </Fragment>
          )}

          <div className={`${styles.extraOptions} col-auto`}>
            <div className="row align-items-center">
              {/* Company information (for RPM admins). */}
              {inCompany && userHasCrossCompanyAccess() && (
                <div className="col px-0">
                  <CompanyOptions />
                </div>
              )}

              {/* Menu of user options. */}
              {!PUBLIC_PAGES.includes(props.currentPage) && (
                <div className="col ps-0">
                  <UserOptions />
                </div>
              )}

              {/* Shopping cart button with shopping cart quantity. */}
              {!PUBLIC_PAGES.includes(props.currentPage) && inCompany && (
                <div className="col">
                  <ShoppingCartButton />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

interface Props {
  currentPage: string;
  onOpenNavigationDrawer: () => void;
}
